import { useCallback, useEffect, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { AgentNameValidatorUiOptions } from './schema';
import { useApi } from '@backstage/core-plugin-api';
import { agentGardenApiRef } from '@mercedes-benz/backstage-plugin-aiecosystem';

export { AgentNameValidatorSchema } from './schema';

type ValidationState =
  | 'idle'
  | 'loading-agents'
  | 'validating'
  | 'valid'
  | 'invalid'
  | 'error';

export const AgentNameValidator = (
  props: FieldExtensionComponentProps<string, AgentNameValidatorUiOptions>,
) => {
  const {
    onChange,
    rawErrors,
    required,
    schema: {
      title = 'Agent Name',
      description = 'Input only letters, numbers, and spaces. No dashes (-) permitted.',
      maxLength = 40, // read value from template, but default to 40
    },
    uiSchema,
    formData,
    formContext, // Access to the entire form data for extracting agent ID
  } = props;

  // Extract UI options from the schema
  const uiOptions =
    (uiSchema?.['ui:options'] as AgentNameValidatorUiOptions) ?? {};
  const debounceMs = uiOptions.debounceMs ?? 400;
  const isEditMode = uiOptions.isEditMode ?? false;

  const agentGardenApi = useApi(agentGardenApiRef);
  const [agentName, setAgentName] = useState<string>(formData ?? '');
  const [validationState, setValidationState] =
    useState<ValidationState>('idle');
  const [validationError, setValidationError] = useState<string | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Save Name from formData (normalized: lowercase, no spaces)
  const originalNameRef = useRef<string | null>(
    formData ? formData.trim().toLowerCase().replace(/\s+/g, '') : null,
  );

  // Extract agent ID from formContext if in Edit mode
  // This ID will be used as exclude_agent_id parameter in the API call
  const agentIdRef = useRef<string | null>(
    isEditMode && formContext?.formData?.agentId
      ? formContext.formData.agentId
      : null,
  );

  /**
   * Performs validation of the agent name against the Agent Garden API.
   * Implements debouncing to avoid excessive API calls while typing.
   * Handles both Edit mode (allows keeping the same name) and Onboarding mode (all existing names blocked).
   *
   * @param value - The agent name to validate
   */
  const performValidation = useCallback(
    (value: string) => {
      // Empty value handling
      if (!value || value.trim().length === 0) {
        setValidationState('error');
        setValidationError('Error. This field is required.');
        onChange(undefined as any);
        return;
      }

      // Length validation
      if (value.length > maxLength) {
        setValidationState('invalid');
        setValidationError(
          `The agent name can be up to ${maxLength} characters long. Current: ${value.length} characters.`,
        );
        onChange(undefined as any);
        return;
      }

      if (!/^[a-zA-Z0-9 ]+$/.test(value)) {
        setValidationState('error');
        setValidationError('Only letters, numbers, and spaces allowed.');
        onChange(undefined as any);
        return;
      }

      // Clear any existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      setValidationState('validating');
      setValidationError(null);

      // Debounced validation logic
      debounceTimerRef.current = setTimeout(async () => {
        const normalized = value.toLowerCase().replace(/\s+/g, '');

        // In Edit mode: Check if new name equals saved name from formData
        // If yes, skip API call as the name hasn't changed
        if (isEditMode && normalized === originalNameRef.current) {
          setValidationState('valid');
          setValidationError(null);
          onChange(value);
          return;
        }

        try {
          // Call API with optional exclude_agent_id parameter
          // In Edit mode: Pass the agent ID to allow keeping the same name
          // In Onboarding mode: Don't pass agent ID, all existing names are blocked
          const result = await agentGardenApi.getAgentNameAvailability(
            value,
            isEditMode && agentIdRef.current ? agentIdRef.current : undefined,
          );

          if (result.available) {
            setValidationState('valid');
            setValidationError(null);
            onChange(value);
          } else {
            setValidationState('invalid');
            const errorMessage = `Error. The agent name "${value}" already exists. Please provide a different name for your agent.`;
            setValidationError(errorMessage);
            onChange(undefined as any);
          }
        } catch (err) {
          setValidationState('error');
          const errorMessage =
            err instanceof Error
              ? err.message
              : 'Unknown error occurred while checking name availability.';
          setValidationError(errorMessage);
          onChange(undefined as any);
        }
      }, debounceMs);
    },
    [agentGardenApi, debounceMs, isEditMode, maxLength, onChange],
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Perform initial validation if formData is present (e.g., after "Start over")
  useEffect(() => {
    if (formData && formData.trim().length > 0) {
      performValidation(formData);
    }
  }, [formData, performValidation]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    setAgentName(val);
    performValidation(val);
  };

  const hasRawErrors = rawErrors && rawErrors.length > 0;
  const isInvalid =
    hasRawErrors ||
    validationState === 'invalid' ||
    validationState === 'error';
  const isValid = validationState === 'valid';
  const isValidating = validationState === 'validating';

  const renderEndAdornment = () => {
    if (isValidating) {
      return (
        <InputAdornment position="end">
          <CircularProgress size={20} />
        </InputAdornment>
      );
    }

    if (isValid && agentName.trim().length > 0) {
      return (
        <InputAdornment position="end">
          <CheckCircleIcon color="success" />
        </InputAdornment>
      );
    }

    if (isInvalid) {
      return (
        <InputAdornment position="end">
          <ErrorOutlineIcon color="error" />
        </InputAdornment>
      );
    }

    return undefined;
  };

  const getHelperText = () => {
    if (validationError) {
      return validationError;
    }

    if (hasRawErrors) {
      return rawErrors[0];
    }

    if (isValidating) {
      return isEditMode ? '' : 'Checking availability...';
    }

    if (isValid && agentName.trim().length > 0) {
      if (
        isEditMode &&
        originalNameRef.current &&
        agentName.trim().toLowerCase().replace(/\s+/g, '') ===
          originalNameRef.current
      ) {
        return '';
      }
      return `Success! The agent name "${agentName}" is available.`;
    }

    return description;
  };

  return (
    <Box>
      <TextField
        fullWidth
        label={title}
        placeholder={uiSchema?.['ui:placeholder'] ?? 'e.g., my agent'}
        required={required}
        value={agentName}
        onChange={handleInputChange}
        disabled={isEditMode}
        variant="standard"
        error={isInvalid}
        color={isValid ? 'success' : undefined}
        focused={isValid}
        helperText={getHelperText()}
        InputProps={{
          endAdornment: renderEndAdornment(),
        }}
        inputProps={{
          pattern: '^[a-zA-Z0-9 ]+$',
          title: 'Only letters, numbers, and spaces allowed.',
          maxLength: maxLength,
        }}
      />
    </Box>
  );
};
